/**
 * CustomLayerRenderer Component
 *
 * Renders custom user-uploaded map layers on the MapLibre map (authenticated
 * workspace map). Lazy-loads GeoJSON when a layer becomes visible via the
 * shared {@link useCustomLayerGeoJson} hook (LRU cache + abort) and renders it
 * with the shared {@link CustomLayerSource}. The public viewer has a parallel
 * renderer that swaps only the fetcher.
 */

import { memo, useCallback } from 'react';
import type { FeatureCollection } from 'geojson';

import { useGeoMarkupStore } from '@/store';
import { selectVisibleCustomLayers } from '@/store/selectors';
import { apiClient } from '@/services/api';
import { API_ENDPOINTS } from '@/constants/api';
import type { MapLayer } from '@/types/layers';

import { CustomLayerSource } from './CustomLayerSource';
import { useCustomLayerGeoJson } from './useCustomLayerGeoJson';

/** Renders a single custom layer with its GeoJSON data (workspace API source). */
const SingleLayerRenderer = memo(function SingleLayerRenderer({
  layer,
}: {
  layer: MapLayer;
}) {
  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await apiClient.get<FeatureCollection>(
        API_ENDPOINTS.LAYERS.GEOJSON(layer.id),
        { signal }
      );
      return response.data;
    },
    [layer.id]
  );

  const geoJson = useCustomLayerGeoJson(`auth:${layer.id}`, fetcher);

  if (!geoJson) return null;

  return (
    <CustomLayerSource
      layerId={layer.id}
      geoJson={geoJson}
      styleJson={layer.styleJson}
    />
  );
});

export const CustomLayerRenderer = memo(function CustomLayerRenderer() {
  const visibleLayers = useGeoMarkupStore(selectVisibleCustomLayers);

  if (visibleLayers.length === 0) return null;

  return (
    <>
      {visibleLayers.map((layer) => (
        <SingleLayerRenderer key={layer.id} layer={layer} />
      ))}
    </>
  );
});
